import React, { createContext, useContext, useState, useEffect } from 'react';

type UserType = 'candidato' | 'empresa' | 'prefeitura';

interface User {
  id: number;
  nome: string;
  email?: string;
  telefone?: string;
  tipo: UserType;
  cpf?: string;  // Para candidatos
  cnpj?: string; // Para empresas
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
  user: User | null;
  token: string | null;
  userType: UserType | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('candidatoToken');
    const userData = localStorage.getItem('userData');

    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);

      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setUserType(parsedUser.tipo);
      } else {
        // Fallback: try to reconstruct user data from session/local storage
        const candidatoId = sessionStorage.getItem('candidatoId');
        const candidatoNome = sessionStorage.getItem('candidatoNome');

        if (candidatoId && candidatoNome) {
          const candidatoUser: User = {
            id: parseInt(candidatoId),
            nome: candidatoNome,
            tipo: 'candidato'
          };
          setUser(candidatoUser);
          setUserType('candidato');
        } else {
          const empresaData = localStorage.getItem('empresaData');
          if (empresaData) {
            const parsedEmpresa = JSON.parse(empresaData);
            setUser({ ...parsedEmpresa, tipo: 'empresa' });
            setUserType('empresa');
          }
        }
      }
    }
  }, []);

  // Chaves de cada perfil. Ao logar num perfil, as dos OUTROS são apagadas para
  // que a nova sessão não herde dados (id, nome, tipo) da sessão anterior do
  // mesmo navegador — ex.: prefeitura caindo no dashboard da empresa zerado.
  const CHAVES_EMPRESA = [
    'empresaData', 'empresaId', 'empresaNome', 'empresaEmail', 'empresaCnpj', 'empresaTelefone',
  ];
  const CHAVES_PREFEITURA = [
    'prefeituraData', 'prefeituraId', 'prefeituraNome', 'prefeituraEmail',
    'prefeituraTelefone', 'prefeituraWhatsapp', 'userName', 'userEmail', 'userCnpj',
  ];
  const CHAVES_CANDIDATO_SESSION = ['candidatoToken', 'candidatoId', 'candidatoNome'];

  const limparChaves = (chaves: string[]) => {
    chaves.forEach((chave) => {
      localStorage.removeItem(chave);
      sessionStorage.removeItem(chave);
    });
  };

  const limparSessaoDeOutrosPerfis = (tipo: UserType) => {
    // `authToken` tem prioridade sobre `token` no interceptor do axios: um
    // resíduo dele mandaria o token de outra sessão
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');

    if (tipo === 'empresa') {
      limparChaves(CHAVES_PREFEITURA);
      limparChaves(CHAVES_CANDIDATO_SESSION);
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('token');
    } else if (tipo === 'prefeitura') {
      limparChaves(CHAVES_EMPRESA);
      limparChaves(CHAVES_CANDIDATO_SESSION);
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('token');
    } else if (tipo === 'candidato') {
      // O interceptor lê `localStorage.token` ANTES de `candidatoToken`: sem
      // esta limpeza o candidato enviaria o token da empresa/prefeitura
      limparChaves(CHAVES_EMPRESA);
      limparChaves(CHAVES_PREFEITURA);
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      localStorage.removeItem('userId');
      localStorage.removeItem('userType');
    }
  };

  const login = (authToken: string, userData: User) => {
    setIsLoading(true);

    setToken(authToken);
    setUser(userData);
    setUserType(userData.tipo);

    limparSessaoDeOutrosPerfis(userData.tipo);

    // Store based on user type
    if (userData.tipo === 'candidato') {
      // Para candidatos, usar sessionStorage
      sessionStorage.setItem('candidatoToken', authToken);
      sessionStorage.setItem('candidatoId', userData.id.toString());
      sessionStorage.setItem('candidatoNome', userData.nome);
      sessionStorage.setItem('userData', JSON.stringify(userData));
    } else if (userData.tipo === 'empresa') {
      // Para empresas, usar localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('empresaData', JSON.stringify(userData));
      localStorage.setItem('empresaId', userData.id.toString());
      localStorage.setItem('empresaNome', userData.nome);
      localStorage.setItem('empresaEmail', userData.email || '');
      localStorage.setItem('empresaCnpj', userData.cnpj || '');
      localStorage.setItem('empresaTelefone', userData.telefone || '');
      // Sobrescreve `userId`/`userType` de uma sessão anterior de outro perfil
      // no mesmo navegador (o login da empresa não gravava estas chaves)
      localStorage.setItem('userId', userData.id.toString());
      localStorage.setItem('userType', 'empresa');
    } else if (userData.tipo === 'prefeitura') {
      // Para prefeitura, usar localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('userId', userData.id.toString());
      localStorage.setItem('userType', 'prefeitura');
    }

    setIsAuthenticated(true);
    setIsLoading(false);
  };

  const logout = () => {
    // Clear all auth data from both storages
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('empresaData');
    localStorage.removeItem('empresaId');
    localStorage.removeItem('empresaNome');
    localStorage.removeItem('empresaEmail');
    localStorage.removeItem('empresaCnpj');
    localStorage.removeItem('empresaTelefone');
    //localStorage.removeItem('empresaEndereco');

    // Chaves genéricas e de prefeitura: sem isto, `userId` sobrevivia ao logout e
    // era lido pelo `obterIdEmpresa` no login seguinte
    localStorage.removeItem('userId');
    localStorage.removeItem('userType');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userCnpj');
    localStorage.removeItem('prefeituraData');
    localStorage.removeItem('prefeituraId');
    localStorage.removeItem('prefeituraNome');
    localStorage.removeItem('prefeituraEmail');
    localStorage.removeItem('prefeituraTelefone');
    localStorage.removeItem('prefeituraWhatsapp');

    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userType');
    sessionStorage.removeItem('candidatoToken');
    sessionStorage.removeItem('candidatoId');
    sessionStorage.removeItem('candidatoNome');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('empresaData');
    sessionStorage.removeItem('empresaId');
    sessionStorage.removeItem('empresaNome');
    sessionStorage.removeItem('empresaEmail');
    sessionStorage.removeItem('empresaCnpj');
    sessionStorage.removeItem('empresaTelefone');
    //sessionStorage.removeItem('empresaEndereco');

    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setUserType(null);
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    user,
    token,
    userType,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};